import type { Camera } from "@/renderer/camera";
import type { Rect } from "@/lib/math/rect";
import type { InvalidationRegion } from "./invalidation";

export interface FrameTiles {
  display: RenderTile[];
  rasterize: RenderTile[];
  /** True when visible tiles were skipped due to {@link TileFrameOptions.maxRasterize}. */
  incomplete: boolean;
}

export interface RenderTile {
  key: string;
  variantKey: string;
  zoomBucket: number;
  tileX: number;
  tileY: number;
  bounds: Rect;
  texture: WebGLTexture;
  framebuffer: WebGLFramebuffer;
  pixelSize: number;
  dirty: boolean;
  lastUsedFrame: number;
  /** When compositing from a different bucket while the target tile rasterizes. */
  compositeScale: number;
  sourceZoomBucket: number;
  /** World bounds the source texture represents when UV subrect mapping is required. */
  sourceBounds?: Rect;
}

export interface TileFrameOptions {
  /** Stable zoom bucket for tile keys/grid; camera zoom may differ during gestures. */
  tileZoomBucket: number;
  /**
   * Cap how many missing/dirty tiles to allocate+queue for raster this frame.
   * `0` = display clean tiles only (no alloc). `undefined` = unlimited (full path).
   * Camera gestures use a small budget so pan/zoom-out stay responsive; idle full
   * fill (and continuations) catch up without cross-bucket fallback glitches.
   */
  maxRasterize?: number;
}

export class TileManager {
  private readonly dirtyRegions: InvalidationRegion[] = [];
  private readonly tiles = new Map<string, RenderTile>();
  private frameIndex = 0;
  private readonly maxTiles = 256;
  private lastVisibleKeys = new Set<string>();

  constructor(readonly tileSize = 512) {}

  invalidate(bounds: Rect, reason: string): void {
    this.dirtyRegions.push({ bounds, reason });
  }

  invalidateEverything(): void {
    this.dirtyRegions.length = 0;
    for (const tile of this.tiles.values()) {
      tile.dirty = true;
    }
  }

  beginFrame(
    gl: WebGL2RenderingContext,
    camera: Camera,
    canvas: HTMLCanvasElement,
    variantKey: string,
    options: TileFrameOptions,
  ): FrameTiles {
    this.frameIndex += 1;
    const targetBucket = options.tileZoomBucket;
    let rasterBudget = options.maxRasterize;
    const descriptors = this.visibleTileDescriptors(camera, canvas, variantKey, targetBucket);
    const visibleKeys = new Set<string>();
    const display: RenderTile[] = [];
    const rasterize: RenderTile[] = [];
    let incomplete = false;

    for (const descriptor of descriptors) {
      visibleKeys.add(descriptor.key);
      const existing = this.tiles.get(descriptor.key);
      if (existing) {
        existing.lastUsedFrame = this.frameIndex;
        if (this.dirtyRegions.some(region => rectsIntersect(existing.bounds, region.bounds))) {
          existing.dirty = true;
        }
        if (!existing.dirty) {
          existing.compositeScale = 1;
          existing.sourceZoomBucket = existing.zoomBucket;
          existing.sourceBounds = undefined;
          display.push(existing);
          continue;
        }
      }

      if (rasterBudget !== undefined && rasterBudget <= 0) {
        incomplete = true;
        continue;
      }

      const exact = this.ensureTile(gl, descriptor);
      exact.lastUsedFrame = this.frameIndex;
      if (this.dirtyRegions.some(region => rectsIntersect(exact.bounds, region.bounds))) {
        exact.dirty = true;
      }
      if (exact.dirty) {
        rasterize.push(exact);
        if (rasterBudget !== undefined) {
          rasterBudget -= 1;
        }
      } else {
        exact.compositeScale = 1;
        exact.sourceZoomBucket = exact.zoomBucket;
        exact.sourceBounds = undefined;
        display.push(exact);
      }
    }

    this.lastVisibleKeys = visibleKeys;
    this.evictColdTiles(gl, visibleKeys);
    this.dirtyRegions.length = 0;

    return { display, rasterize, incomplete };
  }

  dispose(gl: WebGL2RenderingContext): void {
    for (const tile of this.tiles.values()) {
      gl.deleteTexture(tile.texture);
      gl.deleteFramebuffer(tile.framebuffer);
    }
    this.tiles.clear();
    this.dirtyRegions.length = 0;
    this.lastVisibleKeys.clear();
    this.frameIndex = 0;
  }

  private visibleTileDescriptors(
    camera: Camera,
    canvas: HTMLCanvasElement,
    variantKey: string,
    zoomBucket: number,
  ): Array<TileDescriptor & { tileX: number; tileY: number }> {
    const worldTileSize = this.tileSize / zoomBucket;
    const scale = camera.zoom * camera.devicePixelRatio;
    const viewport: Rect = {
      x: camera.x,
      y: camera.y,
      width: canvas.width / scale,
      height: canvas.height / scale,
    };
    const startX = Math.floor(viewport.x / worldTileSize);
    const endX = Math.floor((viewport.x + viewport.width) / worldTileSize);
    const startY = Math.floor(viewport.y / worldTileSize);
    const endY = Math.floor((viewport.y + viewport.height) / worldTileSize);
    const descriptors: Array<TileDescriptor & { tileX: number; tileY: number }> = [];

    for (let tileY = startY; tileY <= endY; tileY += 1) {
      for (let tileX = startX; tileX <= endX; tileX += 1) {
        const key = `${variantKey}:${zoomBucket}:${tileX}:${tileY}`;
        descriptors.push({
          key,
          variantKey,
          zoomBucket,
          pixelSize: this.tileSize,
          tileX,
          tileY,
          bounds: {
            x: tileX * worldTileSize,
            y: tileY * worldTileSize,
            width: worldTileSize,
            height: worldTileSize,
          },
        });
      }
    }

    return descriptors;
  }

  private ensureTile(gl: WebGL2RenderingContext, descriptor: TileDescriptor): RenderTile {
    const cached = this.tiles.get(descriptor.key);
    if (cached) {
      return cached;
    }

    const texture = gl.createTexture();
    const framebuffer = gl.createFramebuffer();
    if (!texture || !framebuffer) {
      if (texture) {
        gl.deleteTexture(texture);
      }
      if (framebuffer) {
        gl.deleteFramebuffer(framebuffer);
      }
      throw new Error("Failed to allocate render tile.");
    }

    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, descriptor.pixelSize, descriptor.pixelSize, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);

    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
      gl.deleteTexture(texture);
      gl.deleteFramebuffer(framebuffer);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      throw new Error("Render tile framebuffer is incomplete.");
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    const tile: RenderTile = {
      ...descriptor,
      texture,
      framebuffer,
      dirty: true,
      lastUsedFrame: this.frameIndex,
      compositeScale: 1,
      sourceZoomBucket: descriptor.zoomBucket,
    };
    this.tiles.set(descriptor.key, tile);
    return tile;
  }

  private evictColdTiles(gl: WebGL2RenderingContext, visibleKeys: Set<string>): void {
    if (this.tiles.size <= this.maxTiles) {
      return;
    }

    const coldTiles = [...this.tiles.values()]
      .filter(tile => !visibleKeys.has(tile.key))
      .sort((left, right) => left.lastUsedFrame - right.lastUsedFrame);
    for (const tile of coldTiles) {
      if (this.tiles.size <= this.maxTiles) {
        break;
      }
      gl.deleteTexture(tile.texture);
      gl.deleteFramebuffer(tile.framebuffer);
      this.tiles.delete(tile.key);
    }
  }
}

interface TileDescriptor {
  key: string;
  variantKey: string;
  zoomBucket: number;
  tileX: number;
  tileY: number;
  bounds: Rect;
  pixelSize: number;
}

function rectsIntersect(left: Rect, right: Rect): boolean {
  return left.x < right.x + right.width &&
    left.x + left.width > right.x &&
    left.y < right.y + right.height &&
    left.y + left.height > right.y;
}
